class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      }
    ];
  }

  /**
   * Helper: find the gift line item that is linked to a specific parent variant.
   * A gift item has properties._gift === 'true' and properties._gift_for_variant === parentVariantId.
   */
  findLinkedGift(items, parentVariantId) {
    return items.find(
      (item) =>
        item.properties &&
        item.properties._gift === 'true' &&
        String(item.properties._gift_for_variant) === String(parentVariantId)
    ) || null;
  }

  /**
   * Helper: apply rendered sections to the DOM.
   */
  applySections(sectionsHtml) {
    this.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;
      const mount = container.querySelector(section.selector) || container;
      const html = sectionsHtml?.[section.section];
      if (!html) return;
      mount.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });
  }

  /**
   * Core updateQuantity — handles parent qty changes and syncs the linked gift 1:1.
   *
   * Flow:
   *  1. Send the user's change to /cart/change.js
   *  2. Inspect the resulting cart state
   *  3. If the changed line is a PARENT (non-gift):
   *     a. quantity > 0  → sync the linked gift to the same new qty (update or add)
   *     b. quantity === 0 (remove) → also remove the linked gift
   *  4. If the changed line is already a GIFT line → just render, no extra sync
   *  5. Render the final sections, update empty-state, dispatch events
   */
  async updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const sectionsToRender = this.getSectionsToRender();
    const sectionKeys = sectionsToRender.map((s) => s.section);

    // Capture DOM qty before the change (used for live-region messaging)
    const quantityElement =
      document.getElementById(`Quantity-${line}`) ||
      document.getElementById(`Drawer-quantity-${line}`);

    // ── Capture variant ID and gift state BEFORE the API call ────────────────
    // When quantity === 0, the item is gone from the response, so we must
    // read the variant ID and existing gift from the DOM / pre-change cart now.
    const lineItemEl =
      document.getElementById(`CartItem-${line}`) ||
      document.getElementById(`CartDrawer-Item-${line}`);
    const domVariantId =
      variantId ||
      lineItemEl?.dataset?.variantId ||
      quantityElement?.dataset?.variantId ||
      lineItemEl?.querySelector('[data-variant-id]')?.dataset?.variantId;

    try {
      // ── Step 0: snapshot cart BEFORE the change ───────────────────────────────
      // When quantity === 0 the removed item no longer appears in firstState.items,
      // so we must capture variant ID and gift linkage now.
      const preChangeCart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json());
      const preChangeItems = preChangeCart.items || [];
      const resolvedVariantId = domVariantId || preChangeItems[line - 1]?.variant_id;
      const preChangeLineItem = preChangeItems[line - 1];
      const isGiftLinePre = preChangeLineItem?.properties?._gift === 'true';

      // ── Step 1: apply the user's change ──────────────────────────────────────
      const firstRes = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          line,
          quantity,
          sections: sectionKeys,
          sections_url: window.location.pathname,
        }),
      });
      const firstText = await firstRes.text();
      const firstState = JSON.parse(firstText);

      if (firstState.errors) {
        if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
        this.updateLiveRegions(line, firstState.errors);
        return;
      }

      const items = firstState.items || [];

      // ── Step 2: determine whether this line is a gift ─────────────────────────
      // Use pre-change check — after removal items[line-1] no longer exists.
      const isGiftLine = isGiftLinePre;

      let finalSections = firstState.sections;

      if (!isGiftLine) {
        // ── Step 3: this is a PARENT line — sync its linked gift ────────────────
        const parentVariantId = resolvedVariantId;
        // Look up the gift from pre-change items (it may already be gone from firstState)
        const linkedGift = this.findLinkedGift(preChangeItems, parentVariantId);
        const newParentQty = Number(quantity); // 0 = remove

        if (newParentQty === 0) {
          // 3b. Parent removed → remove its linked gift (if present)
          if (linkedGift) {
            console.log('[Cart] Parent removed — removing linked gift:', linkedGift.key);
            const removeRes = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                id: linkedGift.key,
                quantity: 0,
                sections: sectionKeys,
                sections_url: window.location.pathname,
              }),
            });
            const removeState = await removeRes.json();
            finalSections = removeState.sections;
          }
        } else {
          // 3a. Parent qty changed (not removed) → sync gift qty 1:1
          if (linkedGift) {
            if (linkedGift.quantity !== newParentQty) {
              console.log(`[Cart] Syncing gift qty ${linkedGift.quantity} → ${newParentQty}`);
              const syncRes = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                  id: linkedGift.key,
                  quantity: newParentQty,
                  sections: sectionKeys,
                  sections_url: window.location.pathname,
                }),
              });
              const syncState = await syncRes.json();
              finalSections = syncState.sections;
            }
            // else: already in sync, no extra call needed
          }
          // No linkedGift found: the gift was already added at add-to-cart time
          // (via the add-to-cart function described in the ticket). Nothing to do here.
        }
      }
      // else: gift line changed directly — do not sync back to parent

      // ── Step 4: render final sections ────────────────────────────────────────
      this.applySections(finalSections);

      // Fetch fresh cart for accurate item_count / empty state
      const freshCart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json());

      this.classList.toggle('is-empty', freshCart.item_count === 0);
      const cartDrawerWrapper = document.querySelector('cart-drawer');
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', freshCart.item_count === 0);
      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', freshCart.item_count === 0);

      // Live-region message
      const cartItems = document.querySelectorAll('.cart-item');
      const updatedValue = freshCart.items?.[line - 1]?.quantity;
      let message = '';
      if (
        cartItems.length === freshCart.items.length &&
        updatedValue !== parseInt(quantityElement?.value, 10)
      ) {
        message =
          typeof updatedValue === 'undefined'
            ? window.cartStrings.error
            : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
      }
      this.updateLiveRegions(line, message);

      // Focus management — guard every trapFocus arg: null container = crash in getFocusableElements
      const lineItem =
        document.getElementById(`CartItem-${line}`) ||
        document.getElementById(`CartDrawer-Item-${line}`);
      const namedInput = name ? lineItem?.querySelector(`[name="${name}"]`) : null;

      if (namedInput) {
        // Item still in DOM — restore focus to its named input
        if (cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, namedInput);
        } else {
          namedInput.focus();
        }
      } else if (freshCart.item_count === 0 && cartDrawerWrapper) {
        // Cart empty — focus empty-state CTA only if both elements actually exist
        const emptyInner = cartDrawerWrapper.querySelector('.drawer__inner-empty');
        const emptyLink  = emptyInner?.querySelector('a');
        if (emptyInner && emptyLink) trapFocus(emptyInner, emptyLink);
      } else if (cartDrawerWrapper && document.querySelector('.cart-item')) {
        // Items remain — focus first item name if it exists
        const firstItemName = document.querySelector('.cart-item__name');
        if (firstItemName) trapFocus(cartDrawerWrapper, firstItemName);
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: freshCart,
        variantId: variantId,
      });

      document.dispatchEvent(
        new CustomEvent('cartUpdated', { detail: { message: 'Cart was updated' } })
      );
    } catch (err) {
      console.error('[Cart] updateQuantity error:', err);
      this.querySelectorAll('.loading__spinner').forEach((ov) => ov.classList.add('hidden'));
      const errors =
        document.getElementById('cart-errors') ||
        document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = window.cartStrings.error;
    } finally {
      this.disableLoading(line);
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}